import { randomUuid } from "@models/utils/uuid";
/**
 * Per-tab app-instance identity — the web stand-in for Unity's
 * `IAppConfig.AppInstanceID`. Stamped as `aid` on every outbound session
 * event and used to drop our own echoes on receive (Unity
 * `AWSServerEventListenerModel.IgnoreEvent` compares the same field).
 *
 * `sessionStorage` scope is deliberate: each browser tab gets its own id
 * (two tabs of the same account behave like two Unity app instances), and
 * the id survives in-tab RELOADS so reconnects keep the same identity.
 *
 * Only reloads, though. Browsers COPY the session storage area into a
 * duplicated tab ("Duplicate Tab") and into any auxiliary context the page
 * opens, so a stored id read back blindly made two LIVE tabs one participant:
 * each dropped the other's poses and lasers as its own echoes, and a single
 * `presenter_change` promoted both. The navigation type tells a reload apart
 * from a duplicate — a duplicate arrives as a plain `navigate` — and is the
 * one synchronous signal available at construction (every consumer captures
 * the id in a field initializer, so a cross-tab handshake cannot be awaited).
 */

const K_STORAGE_KEY = "campfire.web.appInstanceId";

let resolvedId: string | null = null;

export function getAppInstanceId(): string {
  if (resolvedId) return resolvedId;
  const existing = window.sessionStorage.getItem(K_STORAGE_KEY);
  const id = existing && isSameTabContinuation() ? existing : randomUuid();
  window.sessionStorage.setItem(K_STORAGE_KEY, id);
  resolvedId = id;
  return id;
}

/**
 * True when this document continues the SAME tab's previous one — a reload,
 * or a back/forward traversal — rather than being a fresh navigation (which
 * is what a duplicated tab, a `window.open`, or a typed address produce).
 * Unknown (no Navigation Timing entry) is treated as a continuation so the
 * reload behaviour is preserved where the API is missing.
 */
function isSameTabContinuation(): boolean {
  const entry = performance.getEntriesByType("navigation")[0] as
    | PerformanceNavigationTiming
    | undefined;
  if (!entry) return true;
  return entry.type === "reload" || entry.type === "back_forward";
}

/** True when the event payload's `aid` is this tab's own app instance. */
export function isOwnAppInstance(aid: unknown): boolean {
  return typeof aid === "string" && aid.length > 0 && aid === getAppInstanceId();
}
